/**
 * CameraService.js - Utility functions for camera calculations
 * Pure functions for POI position calculations and camera math
 */

import * as THREE from 'three'
import {
  GANTRY_TRANSFORM,
  ROCKET_TRANSFORM,
  LOCAL_POI_POSITIONS,
  BLACK_ARROW_WORLD_POI_POSITIONS,
  getBlackArrowWorldPositions
} from './CameraConfig.js'

// ============================================================================
// LAUNCH PLATFORM POI CALCULATIONS
// ============================================================================

// ============================================================================
// LAUNCH PLATFORM POI CALCULATIONS
// ============================================================================

/**
 * Calculate world positions for launch platform POIs with gantry transformations
 * @returns {Object} World positions for all POI/POS/TAR positions
 */
export function calculateLaunchPlatformPOIPositions(isLandscape = true) {
  const gantryPos = new THREE.Vector3(...GANTRY_TRANSFORM.position)
  const gantryRot = new THREE.Euler(...GANTRY_TRANSFORM.rotation)

  const worldPositions = {}

  // Define which positions to use based on orientation
  const positionKeys = {
    clamp: isLandscape ? 'clamp' : 'clampPortrait',
    fuel: isLandscape ? 'fuel' : 'fuelPortrait',
    shack: isLandscape ? 'shack' : 'shackPortrait'
  }

  // Transform each POI position
  Object.entries(positionKeys).forEach(([baseName, configKey]) => {
    const positions = LOCAL_POI_POSITIONS[configKey]
    if (positions) {
      Object.entries(positions).forEach(([type, localPos]) => {
        const worldPos = localPos.clone()
        worldPos.applyEuler(gantryRot)
        worldPos.add(gantryPos)
        worldPositions[`${baseName}_${type}`] = worldPos
      })
    }
  })

  return worldPositions
}

/**
 * Get launch platform camera position by POI name
 * @param {string} poiName - POI name (e.g., 'POI_LP_Clamp')
 * @param {boolean} isLandscape - True for landscape orientation, false for portrait
 * @returns {Object|null} {position: Vector3, target: Vector3} or null
 */
export function getLaunchPlatformCameraPosition(poiName, isLandscape = true) {
  const worldPositions = calculateLaunchPlatformPOIPositions(isLandscape)

  // Map POI names to camera positions
  const mapping = {
    'POI_LP_Clamp': { pos: 'clamp_POS', tar: 'clamp_TAR' },
    'POI_LP_Fuel': { pos: 'fuel_POS', tar: 'fuel_TAR' },
    'POI_LP_Shack': { pos: 'shack_POS', tar: 'shack_TAR' }
  }

  const keys = mapping[poiName]
  if (!keys) return null

  return {
    position: worldPositions[keys.pos],
    target: worldPositions[keys.tar]
  }
}

// ============================================================================
// BLACK ARROW POI CALCULATIONS
// ============================================================================

/**
 * Calculate camera positions for Black Arrow POIs from config data
 * @param {boolean} isLandscape - Whether the orientation is landscape
 * @returns {Object} Camera positions keyed by POI name
 */
export function calculateBlackArrowPOIPositions(isLandscape = false) {
  const worldPositions = getBlackArrowWorldPositions(isLandscape)
  
  const cameraPositions = {}
  
  // Map local POI keys to POI names
  const poiMapping = {
    'st2': 'POI_BA_ST2',
    'st1': 'POI_BA_ST1',
    'st3': 'POI_BA_ST3',
    'pro': 'POI_BA_PRO'
  }
  
  Object.entries(poiMapping).forEach(([key, poiName]) => {
    const posKey = `${key}_POS`
    const tarKey = `${key}_TAR`
    
    if (worldPositions[posKey] && worldPositions[tarKey]) {
      cameraPositions[poiName] = {
        position: worldPositions[posKey],
        target: worldPositions[tarKey]
      }
    }
  })
  
  return cameraPositions
}

/**
 * Get Black Arrow camera position by POI name from config
 * @param {string} poiName - POI name (e.g., 'POI_BA_ST2')
 * @param {boolean} isLandscape - Whether the orientation is landscape
 * @returns {Object|null} {position: Vector3, target: Vector3} or null
 */
export function getBlackArrowCameraPosition(poiName, isLandscape = false) {
  const cameraPositions = calculateBlackArrowPOIPositions(isLandscape)
  return cameraPositions[poiName] || null
}

// ============================================================================
// SCENE HOTSPOT CALCULATIONS
// ============================================================================

/**
 * Project 3D world position to 2D screen coordinates
 * @param {THREE.Vector3} worldPosition - Position in world space
 * @param {THREE.Camera} camera - Camera to project from
 * @param {Object} size - Viewport size {width, height}
 * @returns {Object} {x, y, isVisible, z} screen coordinates
 */
export function projectPositionToScreen(worldPosition, camera, size) {
  const screenPosition = worldPosition.clone().project(camera)

  // Convert to pixel coordinates
  const x = (screenPosition.x * 0.5 + 0.5) * size.width
  const y = (screenPosition.y * -0.5 + 0.5) * size.height

  // Check if behind camera and within viewport bounds (with margin for dimension lines)
  const margin = 200 // Allow some margin for dimension lines that extend beyond POI position
  const isVisible = screenPosition.z < 1 && 
                   x >= -margin && 
                   x <= size.width + margin && 
                   y >= -margin && 
                   y <= size.height + margin

  return {
    x,
    y,
    isVisible,
    z: screenPosition.z
  }
}

/**
 * Get all hotspots (POIs) for a scene
 * @param {Object} scenesRef - Scene references
 * @param {number} hdriBlend - Scene transition value (0 = scene1, 1 = scene2)
 * @param {THREE.Camera} camera - Camera for projection
 * @param {Object} size - Viewport size
 * @returns {Array} Array of hotspot objects
 */
export function getSceneHotspots(scenesRef, hdriBlend, camera, size) {
  if (!scenesRef || !camera || !size) return []

  const poiGroups = []
  const tarGroups = []
  const hotspots = []

  if (hdriBlend < 1) {
    // Scene 1: Show launch platform POIs
    if (scenesRef.scene1?.current) {
      scenesRef.scene1.current.traverse((object) => {
        if (object.name?.startsWith('POI_LP_')) {
          poiGroups.push(object)
        }
      })
    }
  } else {
    // Scene 2: Show Black Arrow POIs + origin POI
    if (scenesRef.scene2?.current) {
      scenesRef.scene2.current.traverse((object) => {
        if (object.name?.startsWith('POI_BA_') || object.name?.startsWith('BTN_BA_')) {
          poiGroups.push(object)
        }
        if (object.name?.startsWith('TAR_BA_')) {
          tarGroups.push(object)
        }
      })
    }
    
    // Add origin POI at (0,0,0) for Scene 2
    const originPOI = {
      name: 'POI_ORIGIN',
      position: new THREE.Vector3(0, 0, 0)
    }
    poiGroups.push(originPOI)
  }

  if (poiGroups.length === 0) return []

  // Convert POI positions to screen coordinates
  poiGroups.forEach((group) => {
    let worldPosition
    
    // Handle origin POI specially
    if (group.name === 'POI_ORIGIN') {
      worldPosition = group.position.clone()
    } else {
      worldPosition = new THREE.Vector3()
      group.getWorldPosition(worldPosition)
    }

    const screenInfo = projectPositionToScreen(worldPosition, camera, size)

    // For scene 2, find corresponding TAR group
    let tarName = null
    let tarGroup = null
    if (hdriBlend >= 1 && group.name !== 'POI_ORIGIN') {
      tarName = group.name.replace('POI', 'TAR')
      tarGroup = tarGroups.find((tar) => tar.name === tarName)
    }

    hotspots.push({
      name: group.name,
      tarName,
      tarGroup,
      x: screenInfo.x,
      y: screenInfo.y,
      isVisible: screenInfo.isVisible
    })
  })
  
  return hotspots
}

// ============================================================================
// CAMERA ANIMATION HELPERS
// ============================================================================

/**
 * Smooth easing function (ease-in-out cubic)
 * @param {number} progress - Value from 0 to 1
 * @returns {number} Eased value
 */
export function easeInOutCubic(progress) {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2
}
